/**
 * LoadingScreen — interstitial shown while the Roast_Generator is working.
 *
 * Renders a CSS-only conic-gradient spinner (styled in brand.css) plus a
 * `<p>` that cycles through {@link LOADING_MESSAGES} every 3 seconds via
 * {@link selectMessage}. Cross-fade is done with an opacity flip; under
 * `prefers-reduced-motion: reduce` the brand stylesheet collapses
 * transition durations to 0, so the same code path works in both modes.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Animations" and
 * Requirement 3.
 */

import { LOADING_MESSAGES, selectMessage } from '../services/loadingMessages';

/** Rotation cadence for the cheeky-message rotator (AC 3.3). */
const ROTATE_INTERVAL_MS = 3000;
/** Opacity-flip window used for the cross-fade between messages. */
const CROSSFADE_MS = 150;

/**
 * Mount the loading screen into `root`. Returns a teardown function that
 * stops the rotation interval and removes the DOM. Safe to call twice.
 *
 * `now` is injected so tests can drive the elapsed-time calculation with
 * fake timers. Defaults to `Date.now()` in production.
 */
export function mountLoadingScreen(
  root: HTMLElement,
  now: () => number = () => Date.now(),
): () => void {
  const shell = document.createElement('div');
  shell.className = 'roast-shell loading-screen';

  const spinner = document.createElement('div');
  spinner.className = 'roast-spinner';
  spinner.setAttribute('role', 'status');
  spinner.setAttribute('aria-label', 'Loading');

  const message = document.createElement('p');
  message.className = 'roast-loading-message';
  message.setAttribute('aria-live', 'polite');
  message.textContent = selectMessage(0, LOADING_MESSAGES);

  shell.appendChild(spinner);
  shell.appendChild(message);
  root.appendChild(shell);

  const start = now();
  let torn = false;

  /**
   * Advance the rotator: briefly fade out, swap text, fade back in. The
   * swap uses `setTimeout` for the opacity restore so the text change is
   * visible to the user even under fast timers.
   */
  const tick = (): void => {
    if (torn) {
      return;
    }
    const elapsed = now() - start;
    message.style.opacity = '0';
    // Defer the text swap until after the fade-out completes. The CSS
    // transition on `.roast-loading-message` (or 0 ms under reduced motion)
    // smooths the visual change.
    const restoreHandle = setTimeout(() => {
      if (torn) {
        return;
      }
      message.textContent = selectMessage(elapsed, LOADING_MESSAGES);
      message.style.opacity = '1';
    }, CROSSFADE_MS);
    // Track the handle so teardown clears it if we go away mid-fade.
    pendingFades.add(restoreHandle);
  };

  const pendingFades = new Set<ReturnType<typeof setTimeout>>();
  const intervalHandle = setInterval(tick, ROTATE_INTERVAL_MS);

  return () => {
    if (torn) {
      return;
    }
    torn = true;
    clearInterval(intervalHandle);
    for (const handle of pendingFades) {
      clearTimeout(handle);
    }
    pendingFades.clear();
    shell.remove();
  };
}
